import React, { useMemo } from 'react';
import { Card, Table, Button, Space, Tag, Typography, Select, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import stockCountService, { type StockCountSession } from '@/services/stockCount.service';

const { Title, Text } = Typography;

export const StockCountsPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<string | undefined>();
  const [search, setSearch] = React.useState('');

  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ['stock-counts', status, search],
    queryFn: () =>
      stockCountService.list({
        ...(status ? { status } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      }),
  });

  const columns: ColumnsType<StockCountSession> = useMemo(
    () => [
      { title: 'Number', dataIndex: 'count_number', key: 'n', width: 160 },
      { title: 'Date', dataIndex: 'count_date', key: 'd', width: 120 },
      {
        title: 'Warehouse',
        key: 'w',
        render: (_: unknown, r) => r.warehouse?.warehouse_name ?? r.warehouse_id,
      },
      {
        title: 'Blind',
        dataIndex: 'blind_count',
        key: 'b',
        width: 80,
        render: (v: boolean) => (v ? 'Yes' : 'No'),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 's',
        width: 120,
        render: (s: string) => {
          const color =
            s === 'Posted' ? 'green' : s === 'Cancelled' ? 'default' : s === 'Approved' ? 'blue' : 'gold';
          return <Tag color={color}>{s}</Tag>;
        },
      },
      {
        title: '',
        key: 'a',
        width: 100,
        render: (_: unknown, r) => (
          <Button type="link" onClick={() => navigate(`/scm/inventory/stock-count/${r.id}`)}>
            Open
          </Button>
        ),
      },
    ],
    [navigate]
  );

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                Stock count
              </Title>
              <Text type="secondary">
                Physical / cycle count sessions. Variances post through stock adjustment (PHYSICAL_COUNT).
              </Text>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/scm/inventory/stock-count/new')}
            >
              New count
            </Button>
          </Space>
          <Space wrap>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 160 }}
              value={status}
              onChange={setStatus}
              options={[
                { value: 'Draft', label: 'Draft' },
                { value: 'InProgress', label: 'In progress' },
                { value: 'Submitted', label: 'Submitted' },
                { value: 'Approved', label: 'Approved' },
                { value: 'Posted', label: 'Posted' },
                { value: 'Cancelled', label: 'Cancelled' },
              ]}
            />
            <Input.Search
              placeholder="Search count number"
              allowClear
              style={{ width: 260 }}
              onSearch={() => refetch()}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Space>
          <Table<StockCountSession>
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={rows}
            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
          />
        </Space>
      </Card>
    </div>
  );
};

export default StockCountsPage;
